import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  formatPrice,
} from 'common/formatters';

import {
  LEASE_STATUS_SIGNED,
} from 'common/financeConstants';

import {
  SR_API_ACTION_FINANCES_LEASE_CONFIRM_RENTER_MOVED_IN,
  SR_API_ACTION_FINANCES_UPDATE_INVOICE_MERCHANT,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_LEASES,
  DATA_TABLE_VIEW_ID_LEASES_SIGNED_PAST_START_DATE,
  DATA_TABLE_VIEW_ID_LEASES_MISSING_MERCHANT,
} from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadNextPage,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import log from 'client/lib/log';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import SrUpdateableItemValue from 'client/components/SrUpdateableItemValue';
import SrTable from 'client/components/SrTable';
import SrLink from 'client/components/SrLink/SrLink';

function getDataTableSettingsFromProps(props) {
  const dataTableSettings = {};

  switch (props.viewId) {
    case DATA_TABLE_VIEW_ID_LEASES_SIGNED_PAST_START_DATE: {
      dataTableSettings.filter = {
        status: LEASE_STATUS_SIGNED,
      };

      dataTableSettings.customParams = {
        leasesDashboard: {
          pastStartDate: true,
        },
      };
      break;
    }

    case DATA_TABLE_VIEW_ID_LEASES_MISSING_MERCHANT: {
      dataTableSettings.filter = {
        merchantId: null,
      };
      break;
    }
  }

  return dataTableSettings;
}

@dataTable((props) => ({
  dataSource: DATA_TABLE_SOURCE_LEASES,
  viewId: props.viewId,
  sort: {
    createdDate: 'desc',
  },
  ...getDataTableSettingsFromProps(props),
}))
@withInfiniteScroll({
  offset: 32,
})
class SrDataTableLeases extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    viewId: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props);
    const { onInfiniteScrollCallbackReady } = props;
    this._isUnmounting = false;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  componentWillUnmount = () => {
    this._isUnmounting = true;
  }

  _infiniteScrollCallback = () => {
    const { viewId, dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, {
        dataSource: DATA_TABLE_SOURCE_LEASES,
        viewId: viewId,
        count: 8,
      });
    }
  }

  _onRefreshButtonClick = () => {
    const { dispatch } = this.props;
    dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_LEASES,
    });
  }

  _onReloadItemRequested = (itemId) => {
    const { viewId, dispatch } = this.props;
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_LEASES,
      viewId: viewId,
      filter: {
        id: itemId,
      },
      count: 1,
    });
  }

  _onMarkLeaseAsMovedInClick = (event) => {
    const { dispatch } = this.props;
    const leaseId = parseInt(event.target.dataset.leaseId, 10);
    if (!global.confirm('Are you sure you want to confirm the renter moved in?')) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_FINANCES_LEASE_CONFIRM_RENTER_MOVED_IN,
      payload: {
        leaseId,
      },
    }).then(() => {
      if (!this._isUnmounting) {
        this._onReloadLeaseRequested();
      }
    });
  }

  _onUpdateInvoiceMerchantClick = (event) => {
    const { dispatch } = this.props;
    const leaseId = parseInt(event.target.dataset.leaseId, 10);
    const invoiceId = parseInt(event.target.dataset.invoiceId, 10);
    const merchantId = parseInt(global.prompt('Please enter the merchant ID.'), 10);
    if (!merchantId || isNaN(merchantId) || !invoiceId || isNaN(invoiceId)) {
      log.warning('Invalid invoiceId or merchantId', {
        invoiceId,
        merchantId,
      });
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_FINANCES_UPDATE_INVOICE_MERCHANT,
      payload: {
        invoiceId,
        merchantId,
      },
      onError(error) {
        log.error(error);
        global.alert(error.message);
      },
    }).then(() => {
      if (!this._isUnmounting) {
        this._onReloadItemRequested(leaseId);
      }
    });
  }

  render() {
    const {
      items,
      dataTableApiState,
      onInfiniteScrollWrapperRef,
      dispatch,
    } = this.props;

    return (
      <div>
        <div ref={onInfiniteScrollWrapperRef}>
          <button
            type='button'
            onClick={this._onRefreshButtonClick}
            className='floatButton refresh'
          />
          {dataTableApiState.error && (
            <span>ERROR: { dataTableApiState.error }</span>
          )}
          <div className='tableFiltersContainer'>
            <SrLink to='/admin/finances/leases/all'>
              All
            </SrLink>
            <SrLink to='/admin/finances/leases/past-start-date'>
              Unconfirmed move-ins past the start date
            </SrLink>
            <SrLink to='/admin/finances/leases/missing-merchant'>
              Missing Merchant
            </SrLink>
          </div>
          <SrTable
            headers={[
              { content: 'id' },
              { content: 'invoice id' },
              { content: 'renter id' },
              { content: 'merchant id' },
              { content: 'listing id' },
              { content: 'commission amount' },
              { content: 'rent price' },
              { content: 'term' },
              { content: 'status' },
              { content: 'created date' },
              { content: 'start date' },
              { content: 'type' },
              { content: 'actions' },
            ]}
            rows={items.map((lease) => ({
              cells: [
                { content: lease.id },
                { content: lease.invoiceId },
                { content: lease.renterUserId },
                { content: lease.merchantId || (
                  <div
                    data-invoice-id={lease.invoiceId}
                    data-lease-id={lease.id}
                    onClick={this._onUpdateInvoiceMerchantClick}
                  >
                    Add Merchant
                  </div>
                ) },
                { content: (
                  <td>
                    <SrUpdateableItemValue
                      item={lease}
                      dispatch={dispatch}
                      table='leases'
                      fieldId='listingId'
                      validations={['required']}
                      onItemUpdated={this._onReloadLeaseRequested}
                      placeholder='Add Listing'
                    />
                  </td>
                ) },
                { content: formatPrice(lease.commissionAmount) },
                { content: formatPrice(lease.rentPrice) },
                { content: `${lease.term} mo.` },
                { content: lease.status },
                { content: moment(lease.createdDate).format('MMM Do, YYYY - LTS') },
                { content: moment(lease.startDate).format('MMM Do, YYYY') },
                { content: lease.type },
                { content: lease.status !== LEASE_STATUS_SIGNED ? null : (
                  <div data-lease-id={lease.id} onClick={this._onMarkLeaseAsMovedInClick}>
                    Confirm move-in
                  </div>
                ) },
              ],
            }))}
          />
          {dataTableApiState.inProgress && (
            <div className='loading-ellipses left' />
          )}
        </div>
      </div>
    );
  }
}

SrDataTableLeases.displayName = 'SrDataTableLeases';

export default SrDataTableLeases;
